// Investment Description Enhanced Component
// A hybrid description field with text and amenity tiles
// Stores data as JSON string in the description field

import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  forwardRef,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';

import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

// PrimeNG
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';

/**
 * Amenity definition with icon and label
 */
export interface AmenityDefinition {
  id: string;
  label: string;
  icon: string;
  category: AmenityCategory;
}

export type AmenityCategory =
  | 'building'    // Building features
  | 'outdoor'     // Outdoor amenities
  | 'security'    // Security features
  | 'comfort'     // Comfort & convenience
  | 'eco';        // Eco-friendly

/**
 * Enhanced description data structure (stored as JSON string)
 */
export interface EnhancedDescriptionData {
  text: string;
  amenities: string[]; // Array of amenity IDs
}

/**
 * Default amenities list - expandable in the future
 */
export const DEFAULT_AMENITIES: AmenityDefinition[] = [
  // Building features
  { id: 'elevator', label: 'Winda', icon: 'pi-arrow-up', category: 'building' },
  { id: 'underground_parking', label: 'Parking podziemny', icon: 'pi-car', category: 'building' },
  { id: 'storage', label: 'Komórki lokatorskie', icon: 'pi-box', category: 'building' },
  { id: 'bike_storage', label: 'Rowerownia', icon: 'pi-slack', category: 'building' },

  // Outdoor amenities
  { id: 'playground', label: 'Plac zabaw', icon: 'pi-sun', category: 'outdoor' },
  { id: 'pool', label: 'Basen', icon: 'pi-star', category: 'outdoor' },
  { id: 'garden', label: 'Ogród', icon: 'pi-stop', category: 'outdoor' },
  { id: 'terrace', label: 'Taras na dachu', icon: 'pi-home', category: 'outdoor' },

  // Security
  { id: 'monitoring', label: 'Monitoring 24/7', icon: 'pi-video', category: 'security' },
  { id: 'reception', label: 'Recepcja', icon: 'pi-users', category: 'security' },
  { id: 'intercom', label: 'Wideofon', icon: 'pi-phone', category: 'security' },
  { id: 'gated', label: 'Osiedle strzeżone', icon: 'pi-shield', category: 'security' },

  // Comfort
  { id: 'air_conditioning', label: 'Klimatyzacja', icon: 'pi-cloud', category: 'comfort' },
  { id: 'smart_home', label: 'Smart Home', icon: 'pi-mobile', category: 'comfort' },
  { id: 'gym', label: 'Siłownia', icon: 'pi-heart', category: 'comfort' },
  { id: 'concierge', label: 'Concierge', icon: 'pi-user', category: 'comfort' },

  // Eco-friendly
  { id: 'solar_panels', label: 'Panele słoneczne', icon: 'pi-sun', category: 'eco' },
  { id: 'ev_charging', label: 'Ładowarka EV', icon: 'pi-bolt', category: 'eco' },
  { id: 'rainwater', label: 'Retencja wody', icon: 'pi-filter', category: 'eco' },
  { id: 'energy_efficient', label: 'Energooszczędny', icon: 'pi-chart-line', category: 'eco' },
];

/**
 * Category labels for grouping
 */
export const CATEGORY_LABELS: Record<AmenityCategory, string> = {
  building: 'Budynek',
  outdoor: 'Na zewnątrz',
  security: 'Bezpieczeństwo',
  comfort: 'Komfort',
  eco: 'Ekologia',
};

@Component({
  selector: 'app-investment-description-enhanced',
  standalone: true,
  imports: [
    FormsModule,
    TextareaModule,
    TooltipModule,
    RippleModule
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InvestmentDescriptionEnhancedComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="description-enhanced" [class.readonly]="readonly" [class.disabled]="disabled">
      <!-- Text Description Section -->
      <div class="section text-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="pi pi-align-left"></i>
          </div>
          <div class="section-info">
            <h4 class="section-title">Opis inwestycji</h4>
            <p class="section-hint">Opisz inwestycję własnymi słowami</p>
          </div>
        </div>
        <div class="text-editor">
          <textarea
            pInputTextarea
            [ngModel]="textValue()"
            (ngModelChange)="onTextChange($event)"
            [rows]="4"
            [autoResize]="true"
            [disabled]="disabled"
            [readonly]="readonly"
            placeholder="Opisz szczegóły inwestycji, lokalizację, atuty..."
            class="w-full"
          ></textarea>
          <div class="char-count" [class.warning]="textValue().length > 1500">
            {{ textValue().length }} / 2000 znaków
          </div>
        </div>
      </div>

      <!-- Amenities Section -->
      <div class="section amenities-section">
        <div class="section-header">
          <div class="section-icon amenities">
            <i class="pi pi-check-square"></i>
          </div>
          <div class="section-info">
            <h4 class="section-title">Udogodnienia</h4>
            <p class="section-hint">Wybierz dostępne udogodnienia ({{ selectedCount() }} wybranych)</p>
          </div>
        </div>

        <!-- Category Groups -->
        @for (category of categories; track category) {
          <div class="amenity-category">
            <div class="category-label">{{ getCategoryLabel(category) }}</div>
            <div class="amenity-tiles">
              @for (amenity of getAmenitiesByCategory(category); track amenity.id) {
                <button
                  type="button"
                  class="amenity-tile"
                  [class.selected]="isSelected(amenity.id)"
                  [class.disabled]="disabled"
                  [disabled]="disabled || readonly"
                  (click)="toggleAmenity(amenity.id)"
                  [pTooltip]="amenity.label"
                  tooltipPosition="top"
                  pRipple
                >
                  <i [class]="'pi ' + amenity.icon"></i>
                  <span class="amenity-label">{{ amenity.label }}</span>
                  @if (isSelected(amenity.id)) {
                    <span class="check-indicator">
                      <i class="pi pi-check"></i>
                    </span>
                  }
                </button>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .description-enhanced {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      overflow: visible;
    }

    .description-enhanced.readonly {
      pointer-events: none;
      opacity: 0.8;
    }

    .description-enhanced.disabled {
      opacity: 0.6;
    }

    /* Section Styling */
    .section {
      background: var(--p-surface-0);
      border: 1px solid var(--p-surface-200);
      border-radius: 10px;
      padding: 1rem;
      overflow: visible;
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      margin-bottom: 0.75rem;
    }

    .section-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .section-icon.amenities {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    }

    .section-icon i {
      color: white;
      font-size: 0.875rem;
    }

    .section-info {
      flex: 1;
      min-width: 0;
    }

    .section-title {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--p-text-color);
    }

    .section-hint {
      margin: 0.125rem 0 0;
      font-size: 0.75rem;
      color: var(--p-text-secondary-color);
    }

    /* Text Editor */
    .text-editor {
      position: relative;
    }

    .text-editor textarea {
      font-size: 0.875rem;
      line-height: 1.6;
    }

    .char-count {
      position: absolute;
      bottom: 0.5rem;
      right: 0.75rem;
      font-size: 0.625rem;
      color: var(--p-text-secondary-color);
      background: var(--p-surface-0);
      padding: 0.125rem 0.375rem;
      border-radius: 4px;
    }

    .char-count.warning {
      color: #f59e0b;
    }

    /* Amenity Categories */
    .amenity-category {
      margin-bottom: 0.75rem;
    }

    .amenity-category:last-child {
      margin-bottom: 0;
    }

    .category-label {
      font-size: 0.625rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: var(--p-text-secondary-color);
      margin-bottom: 0.375rem;
      padding-left: 0.125rem;
    }

    /* Amenity Tiles Grid */
    .amenity-tiles {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      overflow: visible;
      padding: 4px;
      margin: -4px;
    }

    .amenity-tile {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.625rem;
      background: var(--p-surface-50);
      border: 1px solid var(--p-surface-200);
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s;
      position: relative;
      font-size: 0.75rem;
      color: var(--p-text-color);
      overflow: visible;
    }

    .amenity-tile:hover:not(.disabled) {
      background: var(--p-surface-100);
      border-color: var(--p-surface-300);
    }

    .amenity-tile.selected {
      background: #ecfdf5;
      border-color: #10b981;
      color: #047857;
    }

    .amenity-tile.selected:hover:not(.disabled) {
      background: #d1fae5;
    }

    .amenity-tile.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .amenity-tile i {
      font-size: 0.75rem;
    }

    .amenity-label {
      white-space: nowrap;
    }

    .check-indicator {
      position: absolute;
      top: -6px;
      right: -6px;
      width: 14px;
      height: 14px;
      background: #10b981;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
      z-index: 1;
    }

    .check-indicator i {
      color: white;
      font-size: 0.5rem;
    }

    /* Responsive */
    @media (max-width: 640px) {
      .section {
        padding: 0.75rem;
      }

      .amenity-tile {
        padding: 0.25rem 0.5rem;
        font-size: 0.6875rem;
      }

      .amenity-tile i {
        font-size: 0.6875rem;
      }
    }
  `]
})
export class InvestmentDescriptionEnhancedComponent implements ControlValueAccessor, OnInit {
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() amenities: AmenityDefinition[] = DEFAULT_AMENITIES;

  // Internal state
  private _value: EnhancedDescriptionData = { text: '', amenities: [] };

  textValue = signal('');
  selectedAmenities = signal<string[]>([]);

  categories: AmenityCategory[] = ['building', 'outdoor', 'security', 'comfort', 'eco'];

  // ControlValueAccessor
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  selectedCount = computed(() => this.selectedAmenities().length);

  ngOnInit(): void {
    // Initialize with empty data
  }

  writeValue(value: string | null): void {
    if (value) {
      try {
        const parsed: EnhancedDescriptionData = JSON.parse(value);
        this._value = parsed;
        this.textValue.set(parsed.text || '');
        this.selectedAmenities.set(parsed.amenities || []);
      } catch {
        // If not valid JSON, treat as plain text (backward compatibility)
        this._value = { text: value, amenities: [] };
        this.textValue.set(value);
        this.selectedAmenities.set([]);
      }
    } else {
      this._value = { text: '', amenities: [] };
      this.textValue.set('');
      this.selectedAmenities.set([]);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onTextChange(text: string): void {
    this.textValue.set(text);
    this._value.text = text;
    this.emitChange();
    this.onTouched();
  }

  toggleAmenity(id: string): void {
    const current = this.selectedAmenities();
    const isSelected = current.includes(id);

    if (isSelected) {
      this.selectedAmenities.set(current.filter(a => a !== id));
    } else {
      this.selectedAmenities.set([...current, id]);
    }

    this._value.amenities = this.selectedAmenities();
    this.emitChange();
    this.onTouched();
  }

  isSelected(id: string): boolean {
    return this.selectedAmenities().includes(id);
  }

  getAmenitiesByCategory(category: AmenityCategory): AmenityDefinition[] {
    return this.amenities.filter(a => a.category === category);
  }

  getCategoryLabel(category: AmenityCategory): string {
    return CATEGORY_LABELS[category];
  }

  private emitChange(): void {
    const jsonString = JSON.stringify(this._value);
    this.onChange(jsonString);
  }
}


/**
 * Helper function to parse enhanced description
 */
export function parseEnhancedDescription(value: string | null | undefined): EnhancedDescriptionData {
  if (!value) {
    return { text: '', amenities: [] };
  }

  try {
    return JSON.parse(value);
  } catch {
    // Backward compatibility: plain text
    return { text: value, amenities: [] };
  }
}

/**
 * Helper function to get amenity by ID
 */
export function getAmenityById(id: string): AmenityDefinition | undefined {
  return DEFAULT_AMENITIES.find(a => a.id === id);
}

/**
 * Helper function to get selected amenities as objects
 */
export function getSelectedAmenities(data: EnhancedDescriptionData): AmenityDefinition[] {
  return data.amenities
    .map(id => getAmenityById(id))
    .filter((a): a is AmenityDefinition => a !== undefined);
}
